<template>
    <div class="prepaid-cards">
        <div class="create-container">
            <button class="btn" @click="showCreatePopup">Create</button>
            <button class="btn btn-filter" @click="filterFormVisible = !filterFormVisible">{{ $t('PREPAID_CARDS__SEARCH') }}</button>
            <button class="btn" @click="assignCardVisible = true" v-if="isSupervisor">{{ $t('PREPAID_CARDS__ASSIGN') }}</button>
            <button class="btn" @click="activateCardVisible = true" v-if="isSupervisor">{{ $t('PREPAID_CARDS__ACTIVATE') }}</button>
            <button class="btn export" v-if="dataCount > 0" @click="exportMain">
                <span>{{ $t('PREPAID_CARDS__EXPORT') }}</span>
                <i class="material-icons">cloud_download</i>
            </button>
        </div>
        <form class="filter-container" @submit.prevent="getData" v-show="filterFormVisible">
            <custom-select
                v-model="requestParams.WebShopId"
                :title="$t('PREPAID_CARDS__WEB_SHOP')"
                :options="webshopOptions"
                @clear="requestParams.WebShopId = ''"
                v-if="isSupervisor"
            ></custom-select>
            <custom-input
                :title="$t('PREPAID_CARDS__FROM_ID')"
                inputType="number"
                v-model="requestParams.FromExternalId"
                @clear="requestParams.FromExternalId = ''"
            ></custom-input>
            <custom-input
                :title="$t('PREPAID_CARDS__TO_ID')"
                inputType="number"
                v-model="requestParams.ToExternalId"
                @clear="requestParams.ToExternalId = ''"
            ></custom-input>
            <three-way-checkbox
                v-model="requestParams.Assigned"
                :title="$t('PREPAID_CARDS__ASSIGNED')"
                v-if="isSupervisor"
            ></three-way-checkbox>
            <three-way-checkbox v-model="requestParams.Activated" :title="$t('PREPAID_CARDS__ACTIVATED')"></three-way-checkbox>
            <button class="btn search">{{ $t('PREPAID_CARDS__SEARCH') }}</button>
        </form>
        <grid
            :fullGrid="true"
            :passedData="gridData"
            identificator="id"
            :columns="columns"
            :passedCount="dataCount"
            :passedPageSize="15"
            :title="$t('PREPAID_CARDS__PREPAID_CARDS')"
            @pageChanged="pageChangeHandler"
            @trClicked="trClickHandler"
        ></grid>
        <overlay :visible="createFormVisible" @close="createFormVisible = false">
            <popup
                :title="$t('PREPAID_CARDS__CREATE_PREPAID_CARDS')"
                :expanded="expandedPopup"
                class="create-popup"
                @close="createFormVisible = false"
            >
                <form class="create-form main-form" @submit.prevent="createCards">
                    <custom-input
                        v-model="createCardFormControls.quantity.value"
                        :errorMessage="createCardFormControls.quantity.errorMessage"
                        helperText="Required / Max 1000"
                        :title="$t('PREPAID_CARDS__QUANTITY')"
                        inputType="number"
                        :isError="createCardFormControls.quantity.isError"
                        @clear="createCardFormControls.quantity.value = ''"
                        v-if="isSupervisor"
                    ></custom-input>
                    <custom-select
                        v-model="createCardFormControls.balance"
                        :title="$t('PREPAID_CARDS__BALANCE')"
                        :options="balanceOptions"
                        @clear="createCardFormControls.balance = '5'"
                    ></custom-select>
                    <custom-select
                        v-model="createCardFormControls.webshop"
                        :title="$t('PREPAID_CARDS__WEB_SHOP')"
                        :options="webshopOptions"
                        @clear="createCardFormControls.webshop = ''"
                        v-if="isSupervisor"
                        isRequired
                    ></custom-select>
                    <switcher
                        v-model="createCardFormControls.active"
                        :title="$t('PREPAID_CARDS__ACTIVE')"
                        v-if="isSupervisor"
                    ></switcher>
                    <button class="btn">{{ $t('PREPAID_CARDS__CREATE_CARDS') }}</button>
                </form>
                <loader v-if="loading"></loader>
                <grid
                    v-if="newlyAddedCardsList.length > 0"
                    :fullGrid="true"
                    :passedData="newlyAddedCardsList"
                    identificator="id"
                    :columns="columns"
                    :passedCount="newlyAddedCardsCount"
                    :passedPageSize="10"
                    :title="$t('PREPAID_CARDS__CREATED_CARDS')"
                    @pageChanged="newlyAddedCardsPageChangeHandler"
                    @trClicked="trClickHandler"
                ></grid>
                <div
                    class="btn export"
                    v-if="newlyAddedCardsList.length > 0"
                    @click="exportNewlyAddedToExcel"
                >
                    <span>Export</span>
                    <i class="material-icons">cloud_download</i>
                </div>
            </popup>
        </overlay>
        <overlay :visible="assignCardVisible" @close="assignCardVisible = false">
            <popup
                :title="$t('PREPAID_CARDS__ASSIGN_CARDS')"
                :expanded="assignCardPopupExpanded"
                @close="assignCardVisible = false"
            >
                <assign-card
                    :columns="columns"
                    :webShopOptions="webshopOptions"
                    @expand="assignCardPopupExpanded = true"
                    getCardsURL="WebShop/FilterPrepaidCards"
                    assignCardsURL="WebShop/AssignPrepaidCardsToWebShop"
                />
            </popup>
        </overlay>
        <overlay :visible="activateCardVisible" @close="activateCardVisible = false">
            <popup
                :title="$t('PREPAID_CARDS__ACTIVATE_CARDS')"
                :expanded="activateCardPopupExpanded"
                @close="activateCardVisible = false"
            >
                <activate-card
                    :columns="columns"
                    :webShopOptions="webshopOptions"
                    getCardsURL="WebShop/FilterPrepaidCards"
                    activateCardsURL="WebShop/ActivatePrepaidCards"
                    @expand="activateCardPopupExpanded = true"
                />
            </popup>
        </overlay>
    </div>
</template>

<script>
import axios from '@/Helpers/AxiosInstance.js'
import Grid from '@/components/ReusableComponents/Grid/Grid.vue'
import Overlay from '@/components/ReusableComponents/Overlay/Overlay.vue'
import CustomInput from '@/components/ReusableComponents/CustomInput/CustomInput.vue'
import CustomSelect from '@/components/ReusableComponents/CustomSelect/CustomSelect.vue'
import Loader from '@/components/ReusableComponents/Loader/Loader.vue'
import Switcher from '@/components/ReusableComponents/Switcher/Switcher.vue'
import ThreeWayCheckbox from '@/components/ReusableComponents/ThreeWayCheckbox/ThreeWayCheckbox.vue'
import { addHours } from '@/Helpers/HelperFunctions.js'
import AssignCard from '@/components/AccountCards/AssignCard.vue'
import ActivateCard from '@/components/AccountCards/ActivateCard.vue'
import Popup from '@/components/ReusableComponents/Popup/Popup.vue'
import EventBus from '@/Helpers/EventBus.js'
export default {
    components: {
        Grid,
        Overlay,
        CustomInput,
        CustomSelect,
        Loader,
        Switcher,
        ThreeWayCheckbox,
        AssignCard,
        Popup,
        ActivateCard,
    },
    created() {
        this.getData()
        this.changeFilterValuesIfNotSupervisor()
        this.changeCreateInputsIfNotSupervisor()
    },
    data() {
        return {
            gridData: [],
            requestParams: {
                FromInternalId: '',
                ToInternalId: '',
                Assigned: null,
                Activated: null,
                WebShopId: '',
                FromExternalId: '',
                ToExternalId: '',
                OrderColumn: '',
                OrderDirection: '',
                CurrentPage: 1,
                PageSize: 15,
            },
            assignCardVisible: false,
            activateCardVisible: false,
            assignCardPopupExpanded: false,
            activateCardPopupExpanded: false,
            dataCount: 0,
            createFormVisible: false,
            createCardFormControls: {
                quantity: {
                    value: '',
                    errorMessage: 'Please Enter Quantity',
                    isError: false,
                },
                balance: '5',
                webshop: '',
                active: false,
            },
            loading: false,
            expandedPopup: false,
            fromInternalID: '',
            toInternalID: '',
            newlyAddedCardsList: [],
            newlyAddedCardsRequestParams: {
                FromInternalId: '',
                ToInternalId: '',
                Assigned: '',
                Activated: '',
                WebShopId: '',
                FromExternalId: '',
                ToExternalId: '',
                OrderColumn: '',
                OrderDirection: '',
                CurrentPage: 1,
                PageSize: 10,
            },
            newlyAddedCardsCount: 0,
            filterFormVisible: false,
            balanceOptions: [
                {
                    title: '5',
                    value: '5',
                },
                {
                    title: '10',
                    value: '10',
                },
                {
                    title: '20',
                    value: '20',
                },
                {
                    title: '50',
                    value: '50',
                },
                {
                    title: '100',
                    value: '100',
                },
            ],
        }
    },
    methods: {
        getData() {
            axios
                .get('WebShop/FilterPrepaidCards', {
                    params: { ...this.requestParams },
                })
                .then(resp => {
                    this.gridData = resp.data.response
                    this.dataCount =
                        resp.data.response.length > 0
                            ? resp.data.response[0].count
                            : 0
                })
        },
        pageChangeHandler(page) {
            this.requestParams.CurrentPage = page
            this.getData()
        },
        newlyAddedCardsPageChangeHandler(page) {
            this.newlyAddedCardsRequestParams.CurrentPage = page
            this.getNewlyAddedList()
        },
        createCards() {
            if (this.requestParams.balance == '')
                this.requestParams.balance == 0
            var canSubmit = this.checkErrors(this.createCardFormControls)
            if (!canSubmit && this.isSupervisor) return
            this.loading = true
            axios
                .post('WebShop/CreatePrepaidCards', {
                    quantity: this.createCardFormControls.quantity.value,
                    value: this.createCardFormControls.balance,
                    webShopId: this.createCardFormControls.webshop,
                    activate: this.createCardFormControls.active,
                })
                .then(resp => {
                    this.loading = false
                    this.expandedPopup = true
                    this.newlyAddedCardsRequestParams.FromInternalId =
                        resp.data.response.minId
                    this.newlyAddedCardsRequestParams.ToInternalId =
                        resp.data.response.maxId
                    this.getNewlyAddedList()
                    EventBus.$emit('updateBalance')
                })
        },
        clearCreateForm() {
            this.createCardFormControls.quantity.isError = false
            this.createCardFormControls.quantity.errorMessage = ''
            this.createCardFormControls.quantity.value = ''
            this.createCardFormControls.balance = ''
            this.createCardFormControls.webshop = ''
            this.createCardFormControls.active = false
        },
        checkErrors(form) {
            if (form.quantity.value > 1000) {
                form.quantity.errorMessage = 'Maximum 1000'
                form.quantity.isError = true
                return false
            } else if (form.quantity.value == '') {
                form.quantity.errorMessage = 'Required'
                form.quantity.isError = true
                return false
            }
            form.quantity.isError = false
            return true
        },
        getNewlyAddedList() {
            axios
                .get('WebShop/FilterPrepaidCards', {
                    params: this.newlyAddedCardsRequestParams,
                })
                .then(resp => {
                    this.newlyAddedCardsList = resp.data.response
                    this.newlyAddedCardsCount =
                        resp.data.response.length > 0
                            ? resp.data.response[0].count
                            : 0
                })
        },
        exportNewlyAddedToExcel() {
            axios
                .get('WebShop/FilterPrepaidCards', {
                    params: {
                        ...this.newlyAddedCardsRequestParams,
                        GenerateExcelFile: true,
                    },
                    responseType: 'blob',
                })
                .then(response => {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    )
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'CreatedCards.xlsx')
                    document.body.appendChild(link)
                    link.click()
                })
        },
        exportMain() {
            axios
                .get('WebShop/FilterPrepaidCards', {
                    params: {
                        ...this.requestParams,
                        GenerateExcelFile: true,
                    },
                    responseType: 'blob',
                })
                .then(response => {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    )
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'CreatedCards.xlsx')
                    document.body.appendChild(link)
                    link.click()
                })
        },
        trClickHandler(element, e) {
            if (e.target.matches('.switcher')) {
                axios
                    .post('WebShop/ActivatePrepaidCards', {
                        externalIdFrom: element.externalId,
                        externalIdTo: element.externalId,
                    })
                    .then(resp => {
                        if (resp.data.status == 1) {
                            e.target.classList += ' active'
                            this.getData()
                        }
                    })
            }
        },
        changeFilterValuesIfNotSupervisor() {
            if (!this.isSupervisor && this.webshops.length > 0) {
                this.requestParams.Assigned = true
                this.requestParams.WebShopId = this.webshops[0].id
            }
        },
        changeCreateInputsIfNotSupervisor() {
            if (!this.isSupervisor && this.webshops.length > 0) {
                this.createCardFormControls.quantity.value = 1
                this.createCardFormControls.webshop = this.webshops[0].id
            }
        },
        showCreatePopup() {
            this.createCardFormControls.quantity.value = ''
            this.createCardFormControls.balance = '5'
            this.createCardFormControls.webshop = ''
            this.createCardFormControls.active = false
            this.newlyAddedCardsList = []
            this.expandedPopup = false
            this.createFormVisible = true
        },
    },
    computed: {
        webshops() {
            return this.$store.state.webshops
        },
        webshopOptions() {
            var options = []
            this.webshops.forEach(shop => {
                let title = shop.name
                    ? `${shop.calculatedName} (${shop.name})`
                    : shop.calculatedName
                options.push({ title: title, value: shop.id + '' })
            })
            return options
        },
        isSupervisor() {
            return this.$store.state.shop
        },
        columns() {
            var columns = [
                {
                    title: this.$t('PREPAID_CARDS__ID'),
                    key: 'externalId',
                },
                {
                    title: this.$t('PREPAID_CARDS__BALANCE'),
                    key: 'balanceDecimal',
                    customTemplate: (item, val) => {
                        return val + ' ' + item.currencyCode
                    },
                },
                {
                    title: this.$t('PREPAID_CARDS__CREATE_DATE'),
                    key: 'createTime',
                    customTemplate: (item, val) => addHours(val, 2)
                },
                {
                    title: this.$t('PREPAID_CARDS__PREPAID_CODE'),
                    key: 'prepaidCode',
                },
                {
                    title: this.$t('PREPAID_CARDS__USED'),
                    key: 'used',
                    customTemplate: (item, val) => (val ? 'YES' : 'NO'),
                },
                {
                    title: this.$t('PREPAID_CARDS__ASSIGNED_TO'),
                    key: 'webShopName',
                    customTemplate: (item, val) => (val ? val : '-'),
                },
            ]

            if (!this.isSupervisor) {
                columns.push({
                    title: this.$t('PREPAID_CARDS__ACTIVATE'),
                    key: 'active',
                    customTemplate: (item, val) =>
                        val
                            ? `<span class="success">${this.$t('PREPAID_CARDS__ACTIVATED')}</span>`
                            : `<div class="switcher"></div>`,
                })
            }

            return columns
        },
    },
    watch: {
        createCardFormControls: {
            handler(form) {
                this.checkErrors(form)
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.create-container {
    height: 40 * $rem;
    margin-bottom: 15 * $rem;
    display: flex;

    .btn {
        width: 115 * $rem;
        font-size: 16 * $rem;
        margin-right: 15 * $rem;

        &:last-of-type {
            margin-right: 0;
        }
    }

    .btn.export {
        width: 115 * $rem;
        margin-left: auto;
    }
}

.filter-container {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 15 * $rem;
    background-color: #fff;
    border-radius: 10 * $rem;
    border: 1 * $rem solid #e3e4eb;
    padding: 0 25 * $rem 25 * $rem 25 * $rem;
    align-items: center;

    label,
    .select-wrapper,
    .checkbox-wrapper,
    .switcher-container,
    .btn {
        margin-right: 15 * $rem;
        margin-top: 25 * $rem;
        width: calc(100% / 6 - #{15 * $rem});
    }
}

.create-form {
    display: flex;
    margin-bottom: 40 * $rem;

    label,
    .select-wrapper {
        margin-right: 15 * $rem;
        width: 197 * $rem;
    }

    .btn {
        height: 40 * $rem;
        width: 197 * $rem;
        margin-left: 15 * $rem;
        font-size: 16 * $rem;
    }
}

.btn.export {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    width: 100 * $rem;
    height: 40 * $rem;
}

/deep/ .success {
    color: $green-main;
    font-weight: 600;
    letter-spacing: 0.5 * $rem;
}

.create-popup.expanded {
    width: 70%;
}

@media only screen and (max-width: 1350px) {
    .filter-container {
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            width: calc(33.33% - #{15 * $rem});
        }
    }
}

@media only screen and (max-width: 800px) {
    .create-container {
        margin-top: -15 * $rem;
        margin-bottom: 30 * $rem;
        flex-wrap: wrap;
        height: initial;

        .btn:nth-of-type(3n) {
            margin-right: 0;
        }

        .btn.export {
            margin-left: 0;
        }

        .btn,
        .btn.export {
            margin-top: 15 * $rem;
            width: calc(33% - #{10 * $rem});
        }
    }
}

@media only screen and (max-width: 515px) {
    .filter-container {
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            width: calc(50% - #{15 * $rem});
        }
    }
}

@media only screen and (max-width: 340px) {
    .create-container {
        .btn:nth-of-type(3n) {
            margin-right: 15 * $rem;
        }

        .btn:nth-of-type(2n),
        .btn:nth-of-type(6n) {
            margin-right: 0;
        }

        .btn,
        .btn.export {
            margin-top: 15 * $rem;
            width: calc(50% - #{7.5 * $rem});
        }
    }

    .filter-container {
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            width: calc(100% - #{15 * $rem});
        }
    }
}
</style>
